import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Settings,
  Save,
  Refresh,
  Edit,
  Check,
  Close,
  Store,
  LocalShipping,
  ShoppingBag,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const TokenManagement = () => {
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [tokenSettings, setTokenSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    delivery_prefix: 'DLV',
    takeaway_prefix: 'TAK',
    current_delivery_number: 0,
    current_takeaway_number: 0,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      loadOutlets();
    }
  }, [token]);

  useEffect(() => {
    if (selectedOutlet) {
      loadTokenSettings(selectedOutlet);
    }
  }, [selectedOutlet]);

  const loadOutlets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/outlets/');
      setOutlets(response.data || []);
    } catch (error) {
      console.error('Error loading outlets:', error);
      setError('Failed to load outlets');
    } finally {
      setLoading(false);
    }
  };

  const loadTokenSettings = async (outletId) => {
    try {
      setLoading(true);
      const response = await api.get(`/token-settings/outlet/${outletId}`);
      if (response.data) {
        setTokenSettings(response.data);
        setFormData({
          delivery_prefix: response.data.delivery_prefix || 'DLV',
          takeaway_prefix: response.data.takeaway_prefix || 'TAK',
          current_delivery_number: response.data.current_delivery_number || 0,
          current_takeaway_number: response.data.current_takeaway_number || 0,
        });
      } else {
        // Create default settings if none exist
        setTokenSettings(null);
        setFormData({
          delivery_prefix: 'DLV',
          takeaway_prefix: 'TAK',
          current_delivery_number: 0,
          current_takeaway_number: 0,
        });
      }
    } catch (error) {
      console.error('Error loading token settings:', error);
      setError('Failed to load token settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const payload = {
        outlet_id: selectedOutlet,
        ...formData,
      };

      if (tokenSettings) {
        // Update existing settings
        await api.put(`/token-settings/${tokenSettings.id}`, payload);
      } else {
        // Create new settings
        await api.post('/token-settings/', payload);
      }

      setSuccess('Token settings saved successfully!');
      setEditMode(false);
      await loadTokenSettings(selectedOutlet);
    } catch (error) {
      console.error('Error saving token settings:', error);
      setError('Failed to save token settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    if (tokenSettings) {
      setFormData({
        delivery_prefix: tokenSettings.delivery_prefix || 'DLV',
        takeaway_prefix: tokenSettings.takeaway_prefix || 'TAK',
        current_delivery_number: tokenSettings.current_delivery_number || 0,
        current_takeaway_number: tokenSettings.current_takeaway_number || 0,
      });
    }
  };

  const generatePreviewToken = (prefix, number) => {
    return `${prefix}${String(number + 1).padStart(4, '0')}`;
  };

  const selectedOutletData = outlets.find(outlet => outlet.id === parseInt(selectedOutlet));

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" gutterBottom sx={{ fontSize: '12px' }}>
          Token Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '12px' }}>
          Customize delivery and takeaway token prefixes for each outlet
        </Typography>
      </Box>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Outlet Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px' }}>
          <Store sx={{ fontSize: '12px' }} />
          Select Outlet
        </Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ fontSize: '12px' }}>Choose Outlet</InputLabel>
          <Select
            value={selectedOutlet}
            onChange={(e) => setSelectedOutlet(e.target.value)}
            label="Choose Outlet"
            disabled={loading}
            sx={{ fontSize: '12px' }}
          >
            {outlets.map((outlet) => (
              <MenuItem key={outlet.id} value={outlet.id} sx={{ fontSize: '12px' }}>
                {outlet.name} - {outlet.address}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {selectedOutletData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              Selected:
            </Typography>
            <Chip
              label={selectedOutletData.name}
              color="primary"
              variant="outlined"
              icon={<Store sx={{ fontSize: '12px' }} />}
              sx={{ fontSize: '12px' }}
            />
          </Box>
        )}
      </Paper>

      {/* Token Settings */}
      {selectedOutlet && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px' }}>
              <Settings sx={{ fontSize: '12px' }} />
              Token Settings
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!editMode ? (
                <Button
                  variant="outlined"
                  startIcon={<Edit sx={{ fontSize: '12px' }} />}
                  onClick={() => setEditMode(true)}
                  disabled={loading}
                  sx={{ fontSize: '12px' }}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Save sx={{ fontSize: '12px' }} />}
                    onClick={handleSave}
                    disabled={saving}
                    color="success"
                    sx={{ fontSize: '12px' }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Close sx={{ fontSize: '12px' }} />}
                    onClick={handleCancel}
                    disabled={saving}
                    sx={{ fontSize: '12px' }}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Delivery Settings */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <LocalShipping color="primary" sx={{ fontSize: '12px' }} />
                      <Typography variant="h6" sx={{ fontSize: '12px' }}>Delivery Tokens</Typography>
                    </Box>
                    
                    <TextField
                      fullWidth
                      label="Delivery Prefix"
                      value={formData.delivery_prefix}
                      onChange={(e) => setFormData({ ...formData, delivery_prefix: e.target.value })}
                      disabled={!editMode}
                      size="small"
                      sx={{ mb: 2, fontSize: '12px' }}
                      inputProps={{ maxLength: 10 }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Current Number"
                      type="number"
                      value={formData.current_delivery_number}
                      onChange={(e) => setFormData({ ...formData, current_delivery_number: parseInt(e.target.value) || 0 })}
                      disabled={!editMode}
                      size="small"
                      sx={{ mb: 2, fontSize: '12px' }}
                    />
                    
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '12px' }}>
                        Next Token:
                      </Typography>
                      <Chip
                        label={generatePreviewToken(formData.delivery_prefix, formData.current_delivery_number)}
                        color="primary"
                        variant="filled"
                        size="large"
                        sx={{ fontSize: '12px' }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Takeaway Settings */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <ShoppingBag color="secondary" sx={{ fontSize: '12px' }} />
                      <Typography variant="h6" sx={{ fontSize: '12px' }}>Takeaway Tokens</Typography>
                    </Box>
                    
                    <TextField
                      fullWidth
                      label="Takeaway Prefix"
                      value={formData.takeaway_prefix}
                      onChange={(e) => setFormData({ ...formData, takeaway_prefix: e.target.value })}
                      disabled={!editMode}
                      size="small"
                      sx={{ mb: 2, fontSize: '12px' }}
                      inputProps={{ maxLength: 10 }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Current Number"
                      type="number"
                      value={formData.current_takeaway_number}
                      onChange={(e) => setFormData({ ...formData, current_takeaway_number: parseInt(e.target.value) || 0 })}
                      disabled={!editMode}
                      size="small"
                      sx={{ mb: 2, fontSize: '12px' }}
                    />
                    
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '12px' }}>
                        Next Token:
                      </Typography>
                      <Chip
                        label={generatePreviewToken(formData.takeaway_prefix, formData.current_takeaway_number)}
                        color="secondary"
                        variant="filled"
                        size="large"
                        sx={{ fontSize: '12px' }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Paper>
      )}

      {/* Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<Refresh sx={{ fontSize: '12px' }} />}
          onClick={loadOutlets}
          disabled={loading}
          sx={{ fontSize: '12px' }}
        >
          Refresh
        </Button>
      </Box>
    </Box>
  );
};

export default TokenManagement;
